import actualizarImg from '@assets/actualizar.png'
import logoAtemporal from '@assets/atemporal/LogoAtemporalMenu.svg'
import { HoverZoomPreviewOverlay, useHoverZoomPreview } from '@components/HoverZoomPreview'
import { MENU_INITIAL_KEY } from '@constants'
import type { ViewportLayout } from '@constants'
import { useFetchMenu, type Submenu } from '@hooks/useFetchMenu'
import { useViewportLayout } from '@hooks/useViewportLayout'
import { MenuProxy } from '@proxies/menu'
import * as Tooltip from '@radix-ui/react-tooltip'
import { useAppStore } from '@store'
import { withCameraFitBlocked } from '@utils/camera/withCameraFitBlocked'
import is2DMode from '@utils/is2DMode'
import getId from '@utils/sceneObjects/getId'
import { getRelativeMatrix } from '@utils/sceneObjects/items/getRelativeMatrix'
import {
  RINCONERO_DRAW_DEBUG,
  defaultWidthForDrawCatalog,
  isKubeCushion,
  isModularCornerSnapCatalog,
  resolveAnchorUnitForRinconeroDraw,
} from '@utils/sceneObjects/items/rinconeroUtils'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { Matrix4 } from 'three'
import {
  CATALOG_DIMENSION_ORDER,
  formatCatalogDimensionEntry,
  menuDimensionsToCatalog,
  normalizeMenuDimensionValue,
} from '@utils/dimensions/catalogDimensions'
import { localizeCatalogLabel } from '@utils/i18n/catalogLabels'
import { ui } from '@utils/i18n/uiCopy'

/**
 * Correspondencia ATEMPORAL (mobile responsive Toolbar):
 * - commit 47ee2081: prop `presentation` docked/embedded + densidad de grid/iconos por `useViewportLayout()`.
 * - commit e97d3972: búsqueda profunda de catálogo desde PRINCIPAL (prefetch de hojas de todas las categorías).
 */

type MenuNavEntry = {
  clavemenuabrir: string
  texto: string
}

const filterPrincipalSubmenus = (submenus: Submenu[] = []) =>
  submenus.filter((item) => item.id !== 14 && item.id !== 15)

const filterSubmenuItems = (submenus: Submenu[] = []) => submenus

/** Extrae el alto en cm del título del menú, p. ej. "RESPALDO ALTURA 51" → 51. */
function extractMenuHeightCm(title: string): number | null {
  const match = String(title || '').match(/\b(?:ALTURA|HEIGHT)\s+(\d+)\b/i)
  if (!match) return null
  const height = Number(match[1])
  return Number.isFinite(height) ? height : null
}

/** En menús "ALTURA N" solo deja unidades cuyo Alto de catálogo coincide con N. */
function filterSubmenuItemsByMenuHeight(submenus: Submenu[] = [], menuTitle: string): Submenu[] {
  const expectedHeightCm = extractMenuHeightCm(menuTitle)
  if (expectedHeightCm == null) return submenus
  return submenus.filter(
    (item) => menuDimensionsToCatalog({ alto: item.alto }).alto === expectedHeightCm,
  )
}

/** Densidad del grid de módulos en `embedded` (sheet táctil). Desktop docked no cambia. */
function getEmbeddedModulesGridClass(layout: ViewportLayout): string {
  if (layout === 'mobile') return 'grid grid-cols-2 gap-2'
  if (layout === 'tabletV') return 'grid grid-cols-3 gap-2.5'
  return 'grid grid-cols-4 gap-3' // tabletH
}

function getEmbeddedModuleScale(layout: ViewportLayout): number {
  if (layout === 'mobile') return 0.68
  if (layout === 'tabletV') return 0.72
  return 0.78 // tabletH
}

function getEmbeddedModuleMaxHeight(layout: ViewportLayout): number {
  if (layout === 'mobile') return 72
  if (layout === 'tabletV') return 84
  return 96 // tabletH
}

function toToolbarModuleTitleCase(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase())
}

function isCojinesMenuEntry(entry?: { texto?: string; clavemenuabrir?: string }) {
  const menuKey = String(entry?.clavemenuabrir || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, '')
  const title = String(entry?.texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
  return (
    menuKey.includes('COJINES') ||
    /\bcojines?\b/.test(title) ||
    /\bcushions?\b/.test(title)
  )
}

export function Toolbar({ presentation = 'docked' }: { presentation?: 'docked' | 'embedded' }) {
  const layout = useViewportLayout()
  const { menuData: principalMenuData } = useFetchMenu(MENU_INITIAL_KEY)

  const [navStack, setNavStack] = useState<MenuNavEntry[]>([])
  const currentMenuKey = navStack.at(-1)?.clavemenuabrir ?? MENU_INITIAL_KEY
  const isPrincipalView = navStack.length === 0
  const currentCategoryTitle = navStack.at(-1)?.texto ?? ''
  const hideFondoInMenu = navStack.some(isCojinesMenuEntry)
  const visibleDimensionKeys = hideFondoInMenu
    ? CATALOG_DIMENSION_ORDER.filter((key) => key !== 'fondo')
    : CATALOG_DIMENSION_ORDER

  const { menuData: activeRawMenuData } = useFetchMenu(currentMenuKey, currentMenuKey, null)

  const menuData = (() => {
    if (isPrincipalView) {
      return principalMenuData
        ? {
            ...principalMenuData,
            submenus: filterPrincipalSubmenus(principalMenuData.submenus),
          }
        : principalMenuData
    }
    return activeRawMenuData
      ? {
          ...activeRawMenuData,
          submenus: filterSubmenuItemsByMenuHeight(
            filterSubmenuItems(activeRawMenuData.submenus),
            currentCategoryTitle,
          ),
        }
      : activeRawMenuData
  })()

  const toolbarBreadcrumbInit = useAppStore((s) => s.toolbarBreadcrumbInit)
  const isModalOpen = useAppStore((s) => s.isModalOpen)
  const toolbarMenuToOpen = useAppStore((s) => s.toolbarMenuToOpen)
  const setToolbarMenuToOpen = useAppStore((s) => s.setToolbarMenuToOpen)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [searchText, setSearchText] = useState('')
  /** Catálogo aplanado de módulos dibujables bajo PRINCIPAL (para buscar desde el nivel superior). */
  const [principalCatalog, setPrincipalCatalog] = useState<Submenu[]>([])
  const [principalCatalogStatus, setPrincipalCatalogStatus] = useState<
    'idle' | 'loading' | 'ready' | 'error'
  >('idle')
  const { hoverPreview, getHoverPreviewHandlers } = useHoverZoomPreview()

  const normalizedSearch = searchText.trim().toLowerCase()
  const normalizeWidthToCm = (rawValue: unknown): number | null => {
    const normalized = normalizeMenuDimensionValue(rawValue)
    if (normalized == null || normalized <= 0) return null
    return normalized
  }
  const extractWidthCm = (submenu: any): number | null => {
    const fromDirectField = normalizeWidthToCm(submenu?.ancho)
    if (fromDirectField != null) return fromDirectField

    const sources = [
      submenu?.texto,
      submenu?.descripcion,
      submenu?.referencia,
      ...(Array.isArray(submenu?.args) ? submenu.args : []),
    ]
      .filter(Boolean)
      .map((v) => String(v))
      .join(' ')

    // Ejemplos: "Ancho: 58cm", "ancho 138 cm"
    const match = sources.match(/ancho\s*:?\s*(\d+(?:[.,]\d+)?)\s*cm?/i)
    if (!match) return null
    return normalizeWidthToCm(match[1])
  }

  const numericSearch = Number(normalizedSearch.replace(',', '.'))
  const hasNumericSearch = normalizedSearch.length > 0 && Number.isFinite(numericSearch)

  // Precargar módulos de todas las categorías de PRINCIPAL para que el buscador
  // funcione en el nivel superior (no solo dentro de un grupo abierto).
  useEffect(() => {
    if (!isPrincipalView) return
    const categories = filterPrincipalSubmenus(principalMenuData?.submenus || []).filter((item) =>
      Boolean(item.clavemenuabrir),
    )
    if (categories.length === 0) return

    let cancelled = false
    const proxy = new MenuProxy()
    const idiomaCultura = useAppStore.getState().idiomaCultura || 'ES'

    const fetchLeaves = async (menuKey: string, depth = 0): Promise<Submenu[]> => {
      const data = await proxy.getMenu({ clavemenu: menuKey }, idiomaCultura)
      const items = filterSubmenuItems(Array.isArray(data?.submenus) ? data.submenus : [])
      const leaves: Submenu[] = []
      for (const item of items) {
        if (item.clavemenuabrir && depth < 2) {
          try {
            leaves.push(...(await fetchLeaves(item.clavemenuabrir, depth + 1)))
          } catch {
            /* menú hijo no disponible */
          }
        } else if (!item.clavemenuabrir) {
          leaves.push(item)
        }
      }
      return leaves
    }

    setPrincipalCatalogStatus('loading')
    Promise.all(
      categories.map(async (cat) => {
        try {
          return await fetchLeaves(cat.clavemenuabrir)
        } catch {
          return [] as Submenu[]
        }
      }),
    )
      .then((groups) => {
        if (cancelled) return
        const seen = new Set<string>()
        const flat: Submenu[] = []
        for (const item of groups.flat()) {
          const key = [item.id, item.claveMenuItem, item.codigoUnidad, item.args].join('::')
          if (seen.has(key)) continue
          seen.add(key)
          flat.push(item)
        }
        setPrincipalCatalog(flat)
        setPrincipalCatalogStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setPrincipalCatalogStatus('error')
      })

    return () => {
      cancelled = true
    }
  }, [isPrincipalView, principalMenuData])

  const visibleSubmenus = (() => {
    // En PRINCIPAL con texto de búsqueda: filtrar el catálogo aplanado de todos los grupos.
    const searchingFromPrincipal = isPrincipalView && normalizedSearch.length > 0
    const submenus = searchingFromPrincipal ? principalCatalog : menuData?.submenus || []
    if (!normalizedSearch) return menuData?.submenus || []

    if (hasNumericSearch) {
      const withWidth = submenus
        .map((submenu: any) => ({ submenu, widthCm: extractWidthCm(submenu) }))
        .filter((entry) => entry.widthCm !== null) as { submenu: any; widthCm: number }[]

      const maxWidthLowerOrEqual = withWidth
        .map((entry) => entry.widthCm)
        .filter((width) => width <= numericSearch)
        .reduce((max, width) => (width > max ? width : max), -Infinity)

      if (Number.isFinite(maxWidthLowerOrEqual)) {
        return withWidth
          .filter((entry) => entry.widthCm === maxWidthLowerOrEqual)
          .map((entry) => entry.submenu)
      }

      // Si no hay anchos <= buscado, mantener fallback textual.
    }

    return submenus.filter((submenu: any) => {
      const haystack = [
        submenu?.texto,
        submenu?.claveMenuItem,
        submenu?.codigoUnidad,
        submenu?.referencia,
        submenu?.descripcion,
        ...(Array.isArray(submenu?.args) ? submenu.args : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(normalizedSearch)
    })
  })()

  // Verificar si hay scroll disponible y posición actual
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = scrollContainerRef.current
        const hasScroll = scrollHeight > clientHeight
        setShowScrollButton(hasScroll)

        // Verificar si está exactamente en el inicio o al final (sin margen para ser más estricto)
        const isAtTop = scrollTop === 0
        const isAtBottomPosition = scrollTop + clientHeight >= scrollHeight - 1

        // Solo cambiar la dirección cuando realmente está en los extremos
        if (isAtTop) {
          setIsAtBottom(false) // En el inicio, mostrar flecha hacia abajo
          setHasInitialized(true)
        } else if (isAtBottomPosition) {
          setIsAtBottom(true) // Al final, mostrar flecha hacia arriba
          setHasInitialized(true)
        } else if (!hasInitialized) {
          // Solo en la inicialización, establecer el estado basándose en la posición inicial
          // Si está más cerca del final, mostrar arriba; si no, mostrar abajo
          const distanceFromTop = scrollTop
          const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
          setIsAtBottom(distanceFromBottom < distanceFromTop)
          setHasInitialized(true)
        }
        // Si está en el medio y ya se inicializó, NO cambiar el estado (mantener la dirección actual)
      }
    }

    checkScroll()
    // Verificar cuando cambian los datos del menú
    if (menuData?.submenus) {
      setTimeout(checkScroll, 100)
    }

    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener('scroll', checkScroll)
      window.addEventListener('resize', checkScroll)
      return () => {
        container.removeEventListener('scroll', checkScroll)
        window.removeEventListener('resize', checkScroll)
      }
    }
  }, [visibleSubmenus, hasInitialized])

  // Función para hacer scroll incremental
  const handleScrollClick = () => {
    if (!scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const scrollAmount = 300 // Cantidad de píxeles a scrollear

    if (isAtBottom) {
      // Si está abajo, hacer scroll hacia arriba incrementalmente
      container.scrollBy({
        top: -scrollAmount,
        behavior: 'smooth',
      })
    } else {
      // Si está arriba, hacer scroll hacia abajo incrementalmente
      container.scrollBy({
        top: scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  // Esto para la Sidebar
  useEffect(() => {
    if (!toolbarMenuToOpen || !principalMenuData?.submenus) return

    if (toolbarBreadcrumbInit && toolbarBreadcrumbInit.length > 0) {
      setNavStack(
        toolbarBreadcrumbInit.map((entry) => ({
          clavemenuabrir: entry.clavemenuabrir,
          texto: entry.texto,
        })),
      )
      setToolbarMenuToOpen(null)
      return
    }

    const menuToOpen = principalMenuData.submenus.find(
      (m) => m.clavemenuabrir === toolbarMenuToOpen,
    )
    if (menuToOpen) {
      setNavStack([{ clavemenuabrir: toolbarMenuToOpen, texto: menuToOpen.texto }])
    } else {
      setNavStack([])
    }
    setToolbarMenuToOpen(null)
  }, [toolbarMenuToOpen, principalMenuData?.submenus, setToolbarMenuToOpen, toolbarBreadcrumbInit])

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [currentMenuKey])

  const handleBackNavigation = () => {
    setNavStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : []))
    setSearchText('')
  }

  const handleBackToPrincipal = () => {
    setNavStack([])
    setSearchText('')
  }

  const drawItem = useAppStore((s) => s.drawItem)
  const setMode = useAppStore((s) => s.setMode)
  const mode = useAppStore((s) => s.mode)
  const setCurrentObjectID = useAppStore((s) => s.setCurrentObjectID)
  const unselect = useAppStore((s) => s.unselectAll)
  const select = useAppStore((s) => s.select)
  const setOBB = useAppStore((s) => s.setOBB)
  const replaceItem = useAppStore((s) => s.replaceItem)
  const currentObjectID = useAppStore((s) => s.currentObjectID)
  const items = useAppStore((s) => s.items)
  const selectedIds = useAppStore((s) => s.layers[s.currentLayer].selected)

  const selectedReplaceItem =
    currentObjectID && selectedIds.has(currentObjectID as any)
      ? (items.get(currentObjectID as any) as any)
      : null
  const canShowReplaceButton =
    !!selectedReplaceItem &&
    (selectedReplaceItem.type === 'items' || selectedReplaceItem.type === 'linkedItem')

  const handleMenuItemClick = async (
    clavemenuabrir: string | null,
    texto: string,
    accion?: string | null,
    args?: string[] | null,
    icono?: string,
    claveMenuItem?: string,
  ) => {
    if (isModalOpen) return

    // Si clavemenuabrir es null y accion es "Objeto", dibujar el objeto
    if (!clavemenuabrir && accion?.toLowerCase() === 'objeto' && args && args.length > 0) {
      const catalogID = args.length > 1 ? args[1] : args[0]
      const preDrawStore = useAppStore.getState()
      const anchorIdBeforeUnselect = preDrawStore.currentObjectID

      if (RINCONERO_DRAW_DEBUG) {
        console.warn('[RinconeroDraw][Toolbar.handleMenuItemClick]', {
          catalogID,
          anchorIdBeforeUnselect,
          claveMenuItem,
          note: 'Atemporal dibuja por Toolbar/index, no MenuItem',
        })
      }

      let chainedDrawMatrix: Matrix4 | null = null
      let drawItemType: 'linkedItem' | 'items' = 'items'
      const drawingCushion = isKubeCushion(catalogID)

      if (
        !drawingCushion &&
        anchorIdBeforeUnselect &&
        (String(anchorIdBeforeUnselect).startsWith('linkedItem_') ||
          String(anchorIdBeforeUnselect).startsWith('items_'))
      ) {
        const resolvedAnchorForDraw = resolveAnchorUnitForRinconeroDraw(
          preDrawStore.items as Map<string, any>,
          anchorIdBeforeUnselect as any,
        )
        if (resolvedAnchorForDraw?.unit?.matrix) {
          const resolved = resolvedAnchorForDraw
          const refIsRinconero = isModularCornerSnapCatalog(resolved.unit.catalogID)
          const drawingRinconero = isModularCornerSnapCatalog(catalogID)
          const drawWidth =
            defaultWidthForDrawCatalog(catalogID, preDrawStore.sceneGLB) ||
            Number(resolved.unit.width || 0)

          chainedDrawMatrix = getRelativeMatrix(
            resolved.unit.matrix as Matrix4,
            refIsRinconero || drawingRinconero ? 0 : drawWidth,
            resolved.unit as any,
            undefined,
            undefined,
            catalogID,
          )
          drawItemType = 'linkedItem'
        } else if (RINCONERO_DRAW_DEBUG) {
          console.warn('[RinconeroDraw][Toolbar] sin ancla resuelta', { anchorIdBeforeUnselect })
        }
      } else if (drawingCushion) {
        drawItemType = 'linkedItem'
      } else if (RINCONERO_DRAW_DEBUG && isModularCornerSnapCatalog(catalogID)) {
        console.warn('[RinconeroDraw][Toolbar] sin selección previa — dibujo suelto en origen')
      }

      unselect()
      setMode(
        is2DMode(mode)
          ? 'MODE_FORCE'
          : mode === 'MODE_ALZADOS'
            ? 'MODE_FORCE_ALZADOS'
            : 'MODE_FORCE_3D',
      )

      try {
        const id = getId(drawItemType === 'linkedItem' ? 'linkedItem' : 'items')
        const reid = await drawItem(
          id,
          catalogID,
          icono || '',
          true,
          chainedDrawMatrix,
          drawItemType,
          undefined,
          false,
          drawingCushion ? { preDrawSelectedAnchorId: anchorIdBeforeUnselect as any } : undefined,
        )
        setCurrentObjectID(reid)
        setOBB(reid)
      } finally {
        setTimeout(() => {
          setMode(
            is2DMode(mode)
              ? 'MODE_IDLE'
              : mode === 'MODE_FORCE_ALZADOS'
                ? 'MODE_ALZADOS'
                : 'MODE_3D_VIEW',
          )
        }, 100)
      }
      return
    }

    // Si tiene clavemenuabrir, navegar al submenú en el mismo panel
    if (clavemenuabrir) {
      setNavStack((prev) => [...prev, { clavemenuabrir, texto }])
      setSearchText('')
      return
    }
  }

  return (
    <aside
      ref={scrollContainerRef}
      className={
        presentation === 'embedded'
          ? 'scrollable relative min-h-0 w-full overflow-y-auto overflow-x-hidden bg-white px-3 pb-6 pt-0'
          : 'scrollable fixed bottom-0 left-0 top-20 z-10 w-80 overflow-y-auto overflow-x-hidden  bg-white px-4 pb-24 pt-0'
      }
    >
      <div className='sticky top-0 z-20 mb-4 bg-white pb-3'>
        {presentation !== 'embedded' && isPrincipalView ? (
          <div className='mb-2 rounded-2xl border border-[#D7C9A6]/55 bg-white/95 px-2.5 py-1.5 shadow-[0_6px_16px_rgba(0,0,0,0.06)] backdrop-blur-[2px]'>
            <div className='flex items-center gap-2.5'>
              <span className='flex h-10 w-10 items-center justify-center rounded-full border border-[#C8A85A] bg-gradient-to-b from-[#E1C57A] to-[#C8A85A] text-xl font-extrabold text-white shadow-[0_3px_8px_rgba(200,168,90,0.4)]'>
                1
              </span>
              <div className='leading-tight'>
                <p className='text-base font-semibold tracking-[0.02em] text-C/Color3'>
                  {ui('chooseModules')}
                </p>
              </div>
            </div>
          </div>
        ) : null}
        {!isPrincipalView ? (
          <div className='mb-2 flex items-center gap-2'>
            <button
              type='button'
              onClick={handleBackNavigation}
              className='group flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C8A85A]/70 bg-gradient-to-b from-[#FAF4E8] to-[#F0E4C8] text-[#8B6F2E] shadow-[0_2px_8px_rgba(200,168,90,0.25)] transition-all hover:scale-105 hover:border-[#C8A85A] hover:shadow-[0_4px_12px_rgba(200,168,90,0.35)] active:scale-95'
              aria-label='Volver al menú principal'
            >
              <svg
                className='h-5 w-5 transition-transform group-hover:-translate-x-0.5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2.5}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
            </button>
            <div className='min-w-0 flex-1 leading-tight'>
              <p className='truncate text-base font-semibold tracking-[0.02em] text-C/Color3'>
                {localizeCatalogLabel(currentCategoryTitle)}
              </p>
              <button
                type='button'
                onClick={handleBackToPrincipal}
                className='mt-0.5 text-xs font-medium text-[#8B6F2E] transition-colors hover:text-[#6B5420] hover:underline'
              >
                {ui('backToPrincipal')}
              </button>
            </div>
          </div>
        ) : null}
        <div className='relative'>
          <input
            type='text'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={isPrincipalView ? ui('searchByWidth') : ui('search')}
            className='w-full rounded-xl border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-C/Color3 outline-none transition-all focus:border-colorAtemporal focus:bg-white focus:ring-2 focus:ring-colorAtemporal/20'
          />
          <svg
            className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='m21 21-4.35-4.35m1.85-5.15a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z'
            />
          </svg>
          {searchText && (
            <button
              type='button'
              onClick={() => setSearchText('')}
              className='absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700'
              aria-label='Limpiar búsqueda'
            >
              x
            </button>
          )}
        </div>
      </div>

      {/* Grid: docked histórico de 2 columnas; embedded densifica por mobile / tabletV / tabletH */}
      <div
        className={
          presentation === 'embedded'
            ? getEmbeddedModulesGridClass(layout)
            : 'grid grid-cols-2 gap-5'
        }
      >
        {visibleSubmenus.map(
          (
            {
              id,
              claveMenuItem,
              clavemenuabrir,
              orden,
              texto,
              referencia,
              codigoUnidad,
              descripcion,
              fondo,
              ancho,
              alto,
              icono,
              altoicono,
              anchoicono,
              accion,
              args,
            },
            index,
          ) => {
            // Usar logoAtemporal solo para ATEMPORAL
            const isAtemporal = claveMenuItem === 'ATEMPORAL'
            const finalIcono = isAtemporal ? logoAtemporal : icono
            // Imágenes más grandes para mejor visibilidad
            const finalAltoicono = isAtemporal ? (altoicono ?? 120) : (altoicono ?? 110)
            const finalAnchoicono = isAtemporal ? (anchoicono ?? 180) : (anchoicono ?? 140)
            const embeddedScale = presentation === 'embedded' ? getEmbeddedModuleScale(layout) : 1
            const displayAltoicono = Math.round(finalAltoicono * embeddedScale)
            const displayAnchoicono = Math.round(finalAnchoicono * embeddedScale)
            const embeddedMaxHeight =
              presentation === 'embedded' ? getEmbeddedModuleMaxHeight(layout) : 120
            const refSource = String(referencia || codigoUnidad || texto || '').trim()
            const refNumber = refSource.split('-')[0]?.trim() || refSource
            const catalogDimensions = menuDimensionsToCatalog({ ancho, fondo, alto })
            const showSpecs = Boolean(codigoUnidad || referencia)
            const showModuleSpecs =
              (!isPrincipalView || normalizedSearch.length > 0) &&
              (showSpecs || alto != null || ancho != null || fondo != null)
            const moduleTitle = toToolbarModuleTitleCase(
              localizeCatalogLabel(
                String(descripcion || codigoUnidad || referencia || texto || '').trim() || texto,
              ),
            )
            const displayTexto = localizeCatalogLabel(texto)

            const handleReplaceItem = async (e: React.MouseEvent) => {
              e.stopPropagation()
              if (!currentObjectID) return
              const usingItem = items.get(currentObjectID)
              if (!usingItem) return
              const catalogID =
                args && args.length > 1 ? args[1] : args && args.length > 0 ? args[0] : ''
              const realCatalogID = args && args.length === 2 ? args[0] : undefined
              if (catalogID) {
                await withCameraFitBlocked(async () => {
                  const replacedId = await replaceItem(
                    usingItem.id,
                    catalogID,
                    icono || '',
                    'linkedItem',
                    realCatalogID,
                  )
                  if (replacedId) {
                    unselect()
                    select(replacedId as any, false)
                    setCurrentObjectID(replacedId as string)
                  }
                })
              }
            }

            return (
              <div
                key={id}
                onClick={() =>
                  handleMenuItemClick(clavemenuabrir, texto, accion, args, icono, claveMenuItem)
                }
                className={clsx(
                  'group relative flex flex-col items-center rounded-lg transition-all duration-200',
                  presentation === 'embedded' ? 'px-1 py-2' : 'px-2 py-3',
                  !isModalOpen && 'cursor-pointer hover:bg-gray-50',
                )}
              >
                {/* Botón de reemplazar - solo para objetos */}
                {accion?.toLowerCase() === 'objeto' && canShowReplaceButton && (
                  <Tooltip.Provider>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <button
                          type='button'
                          onClick={handleReplaceItem}
                          className={clsx(
                            'absolute z-10 flex cursor-pointer items-center justify-center rounded-full bg-white shadow-md transition-all hover:scale-110 hover:bg-gray-100 hover:shadow-lg',
                            presentation === 'embedded'
                              ? '-right-1.5 -top-2.5 h-8 w-8'
                              : '-right-1 -top-1 h-7 w-7',
                          )}
                        >
                          <img
                            src={actualizarImg}
                            alt='Reemplazar'
                            className='pointer-events-none h-5 w-5 select-none'
                            draggable='false'
                          />
                        </button>
                      </Tooltip.Trigger>
                      <Tooltip.Content
                        side='left'
                        align='center'
                        className='z-50 rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black outline outline-[0.5px] outline-C/Color3'
                      >
                        {'Sustituye el elemento que tengas seleccionado por este'}
                        <Tooltip.Arrow className='fill-C/Color1' />
                      </Tooltip.Content>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                )}
                {/* Imagen */}
                <div
                  className='flex items-center justify-center'
                  {...(!isModalOpen && finalIcono
                    ? getHoverPreviewHandlers(finalIcono, displayTexto)
                    : {})}
                >
                  <img
                    className='pointer-events-none object-contain transition-transform duration-300 group-hover:scale-[1.02]'
                    style={{
                      height: displayAltoicono,
                      width: displayAnchoicono,
                      maxHeight: presentation === 'embedded' ? embeddedMaxHeight : 120,
                      maxWidth: presentation === 'embedded' ? '100%' : '180px',
                    }}
                    src={finalIcono}
                    alt={displayTexto}
                    loading='lazy'
                  />
                </div>
                {/* Texto justo debajo de la imagen - muy cerca */}
                {showModuleSpecs ? (
                  <div
                    className={clsx(
                      'mt-1 text-C/Color3 transition-colors duration-200',
                      presentation === 'embedded'
                        ? 'mx-auto max-w-full text-center text-xs leading-tight'
                        : 'mx-auto ml-1 text-left text-sm',
                    )}
                    style={{
                      lineHeight: presentation === 'embedded' ? '1.15' : '1.2',
                      width:
                        typeof displayAnchoicono === 'number'
                          ? `${displayAnchoicono}px`
                          : displayAnchoicono,
                    }}
                  >
                    <p className='text-xs font-semibold'>{moduleTitle}</p>
                    {visibleDimensionKeys.map((key) => (
                      <p key={key}>{formatCatalogDimensionEntry(key, catalogDimensions)}</p>
                    ))}
                  </div>
                ) : showSpecs ? (
                  <div
                    className={clsx(
                      'mt-1 text-C/Color3 transition-colors duration-200',
                      presentation === 'embedded'
                        ? 'mx-auto max-w-full text-center text-xs leading-tight'
                        : 'mx-auto ml-1 text-left text-sm',
                    )}
                    style={{
                      lineHeight: presentation === 'embedded' ? '1.15' : '1.2',
                      width:
                        typeof displayAnchoicono === 'number'
                          ? `${displayAnchoicono}px`
                          : displayAnchoicono,
                    }}
                  >
                    <p className='font-semibold'>{`Referencia: ${refNumber}`}</p>
                    {visibleDimensionKeys.map((key) => (
                      <p key={key}>{formatCatalogDimensionEntry(key, catalogDimensions)}</p>
                    ))}
                  </div>
                ) : (
                  <p
                    className={clsx(
                      'mt-1 w-full text-center font-medium text-C/Color3 transition-colors duration-200 group-hover:text-C/Color3',
                      presentation === 'embedded' ? 'text-xs' : 'text-sm',
                    )}
                    style={{
                      wordBreak: 'break-word',
                      lineHeight: '1.2',
                    }}
                  >
                    {displayTexto}
                  </p>
                )}
              </div>
            )
          },
        )}
      </div>
      {visibleSubmenus.length === 0 && (
        <p className='mt-6 text-center text-sm text-gray-500'>
          {isPrincipalView && normalizedSearch.length > 0 && principalCatalogStatus === 'loading'
            ? ui('searchingModules')
            : ui('noResultsFound')}
        </p>
      )}

      {/* Botón circular flotante para scroll - solo docked (en embedded el FAB fixed se solapa con el sheet). */}
      {showScrollButton && presentation !== 'embedded' && (
        <div className='pointer-events-none fixed bottom-3 left-[299px] z-30 flex justify-end pr-2'>
          <button
            onClick={handleScrollClick}
            className={clsx(
              'pointer-events-auto -mr-7 h-10 w-10 rounded-full',
              'bg-gray-200/40 backdrop-blur-sm hover:bg-gray-200/60',
              'flex items-center justify-center transition-all duration-300',
              'border border-gray-300/30 hover:scale-105 active:scale-95',
            )}
            aria-label={isAtBottom ? 'Subir' : 'Bajar'}
          >
            <svg
              className={clsx(
                'h-5 w-5 text-gray-600/80 transition-transform duration-300',
                isAtBottom && 'rotate-180',
              )}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 14l-7 7m0 0l-7-7m7 7V3'
              />
            </svg>
          </button>
        </div>
      )}
      <HoverZoomPreviewOverlay preview={hoverPreview} />
    </aside>
  )
}
